import { COUNTRIES } from "../../constants/common/common";
import { constants } from "../../constants/pageConstants/pdpConstants";
import { InterceptorClient } from "../../interceptor/interceptorClient";
import {
  AWS_PRE_SIGNED_URL,
  CALENDER_MSG_URL,
  GET_PRODUCT_DETAILS_URL,
  GET_PRODUCT_REVIEWS_URL,
  CUSTOMIZABLE_CONTENT_URL,
  GET_PRODUCT_STOCK_AVAILABLE_URL,
  OUT_OF_STOCK_URL,
  PRICE_SURGE_DETAILS_ENDPOINT,
  UPDATE_PRODUCT_DETAILS_URL,
  GET_SALE_NUDGE_CONFIG,
  PDP_SUGGESTION,
  GET_PRODUCT_BY_URL,
  PAY_LATER_CONFIG,
  IS_SWIPE_ENABLE,
  buildPdpBatchApiPaths,
  GET_PDP_BATCH_API_URL,
  mapPdpBatchApiResponse,
  getPdpBatchEmptyMapped,
  PDP_CMS_BATCH_SERVER_CACHE_TTL_MS,
} from "./constants";
import { withServerCache } from "../../utils/cache-layer/serverCache";
import { isInterceptorClientErrorShape, isLikelyCmsBatchPayload } from "../../utils/cmsBatch";

const webBaseUrl = process.env.NEXT_PUBLIC_BASE_URL;
const serverBaseUrl = process.env.NEXT_PUBLIC_SERVER_URL_API;

const basicHeaders = {
  "Access-Control-Allow-Credentials": "true",
  "Access-Control-Allow-Headers": "Content-Type",
  "Content-Type": "text/html;charset=UTF-8",
  "X-made-with": "Made with <3 in India",
};

/**
 * Fetches PDP batch CMS data via CMS server gateway.
 *
 * @param {boolean} isMobile
 * @param {string} catalogId
 * @returns {Promise<Object>}
 */
export const getPdpCmsBatchApi = async (isMobile, catalogId = COUNTRIES.INDIA) => {
  try {
    const pathsArray = buildPdpBatchApiPaths({ isMobile, catalogId });
    const relativeUrl = GET_PDP_BATCH_API_URL(pathsArray);
    const response = await InterceptorClient.getServerCMSInstance().get(relativeUrl, false, null, {
      ...basicHeaders,
      "x-device-type": isMobile ? "mobile" : "desktop",
      "x-domain": "www.fnp.com",
    });

    if (isInterceptorClientErrorShape(response)) {
      throw new Error(`PDP batch HTTP ${response.status}`);
    }
    if (!isLikelyCmsBatchPayload(response)) {
      return {};
    }
    return response;
  } catch (error) {
    console.log("Error fetching PDP batch API", error);
    return {};
  }
};

/**
 * Fetches PDP batch CMS data, maps response, and caches the parsed bundle.
 *
 * @param {Object} args
 * @param {boolean} args.isMobile
 * @param {string} [args.catalogId]
 * @returns {Promise<Object>}
 */
export const getPdpCmsBatchMapped = async ({ isMobile, catalogId = COUNTRIES.INDIA }) => {
  const cacheKey = `pdp:batch:${isMobile ? "m" : "d"}:${catalogId || COUNTRIES.INDIA}`;
  try {
    return await withServerCache(
      cacheKey,
      async () => {
        const raw = await getPdpCmsBatchApi(isMobile, catalogId);
        return mapPdpBatchApiResponse(raw, { isMobile });
      },
      PDP_CMS_BATCH_SERVER_CACHE_TTL_MS,
    );
  } catch (error) {
    console.log("Error fetching mapped PDP batch", error);
    return getPdpBatchEmptyMapped();
  }
};

/**
 * Fetches product Details details from the API.
 * @returns {Promise<unknown>} A promise that resolves to the product Details.
 */
export const getProductDetailsAPI = async (country = "india", urlIdentifier) => {
  try {
    const response = await InterceptorClient.getFTSInstance(serverBaseUrl).get(
      `${GET_PRODUCT_DETAILS_URL(country, urlIdentifier)}`,
      false,
    );
    if (response && response?.status === 200) {
      return response;
    } else {
      return {};
    }
  } catch (error) {
    console.log("Error in getProductDetailsAPI:", error);
    return {};
  }
};

/**
 * Fetches product Details details from the API.
 * @returns {Promise<unknown>} A promise that resolves to the product Details.
 */
export const updateProductDetailsAPI = async (
  productId = "",
  currencyUomId = "INR",
  catalogId = "india",
  categoryId = "",
) => {
  const response = await InterceptorClient.getFTSInstance().get(
    `${UPDATE_PRODUCT_DETAILS_URL(productId, currencyUomId, catalogId, categoryId)}`,
    false,
  );
  return response;
};

/**
 * Fetches get Customer Review details from the API.
 * @returns {Promise<unknown>} A promise that resolves to the getCustomerReviewAPI details.
 */
export const getCustomerReviewAPI = async (
  productId = "",
  domainId = constants.DOMAIN_ID,
  NoOfReviewRequest = constants.NoOfReviewRequest,
) => {
  const res = await InterceptorClient.getGatewayInstance().get(
    `${GET_PRODUCT_REVIEWS_URL(productId, domainId, NoOfReviewRequest)}`,
    false,
  );
  return res;
};

/**
 * Upload Images to AWS S3 Bucket.
 * @returns {Promise<unknown>} A promise that return presigned url.
 */
export const uploadToAwsS3Bucket = async (fileName) => {
  const response = await InterceptorClient.getFTSInstance().get(`${AWS_PRE_SIGNED_URL(fileName)}`, false);
  return response;
};

/**
 * Get Content for Customization of single image
 * @returns {Promise<unknown>} HTML for the customization of product.
 */
export const getCustomizableImageContent = async (params) => {
  const response = await InterceptorClient.getFNTSInstance(webBaseUrl).get(
    `${CUSTOMIZABLE_CONTENT_URL(params)}`,
    false,
  );
  return response;
};

/**
 * Fetches the calendar footer message from the API.
 *
 * @async
 * @function
 * @returns {Promise<Object>} A promise that resolves to the response object containing the calendar footer message.
 */
export const fetchCalendarFooterMsg = async () => {
  return await InterceptorClient.getGatewayInstance().get(`${CALENDER_MSG_URL()}`, false);
};

/**
 * Fetches product stock available details from the API.
 * @returns {Promise<unknown>} A promise that resolves to the product Details.
 */
export const getProductStockAvailableAPI = async (productId = "", isServer = false, pincode) => {
  try {
    const res = await InterceptorClient.getFTSInstance(isServer ? serverBaseUrl : "").get(
      `${GET_PRODUCT_STOCK_AVAILABLE_URL(productId, pincode)}`,
      false,
    );
    if (res && res?.status === 200) {
      return res;
    } else {
      return {};
    }
  } catch (error) {
    console.log("Error in getProductStockAvailableAPI:", error);
    return {};
  }
};

/**
 * Fetches the out of stock redirection urls from the API.
 * @async
 * @function
 * @returns {Promise<Object>} A promise that resolves to the response object containing the urls.
 */
export const getOutOfStockRedirectionUrlsAPI = async () => {
  return await InterceptorClient.getGatewayInstance().get(`${OUT_OF_STOCK_URL()}`, false);
};

/**
 * Fetches the get Price Surge Details from the API.
 * @async
 * @function
 * @returns {Promise<Object>} A promise that resolves to the response object containing the urls.
 */
export const getSurgePriceDetails = async (productId) => {
  return await InterceptorClient.getFTSInstance(process.env.NEXT_PUBLIC_BASE_URL).get(
    `${PRICE_SURGE_DETAILS_ENDPOINT(productId)}`,
    false,
  );
};
/**
 * get lottie icons
 *
 * @returns {Array} return the sale nudge configuration
 */
export async function getSaleNudgeConfig() {
  try {
    const res = await InterceptorClient.getGatewayInstance().get(`${GET_SALE_NUDGE_CONFIG()}`, false);
    return res;
  } catch (ex) {
    throw new Error({ ex, isSuccess: true });
  }
}
/** */
export const getPdpSuggestion = async (productName) => {
  return await InterceptorClient.getGatewayInstance().get(`${PDP_SUGGESTION(productName)}`, false);
};

/**
 * Fetches product details by URL identifier from the search API.
 * Uses native fetch for Edge Runtime compatibility (works in both Node.js and Edge Runtime).
 * @param {string} country - The country code (default: "india").
 * @param {string} urlIdentifier - The product URL identifier/slug.
 * @returns {Promise<Object|null>} A promise that resolves to the product details with productId, or null.
 */
export const getProductByUrlAPI = async (country = "india", urlIdentifier) => {
  // Edge case: urlIdentifier is required
  if (!urlIdentifier || typeof urlIdentifier !== "string" || urlIdentifier.trim() === "") {
    return null;
  }

  try {
    const searchServerBaseUrl = process.env.NEXT_PUBLIC_SEARCH_SERVER_API_GATEWAY;

    // Edge case: baseUrl is undefined or empty
    if (!searchServerBaseUrl) {
      console.log("NEXT_PUBLIC_SEARCH_SERVER_API_GATEWAY is not configured");
      return null;
    }

    const url = `${searchServerBaseUrl}${GET_PRODUCT_BY_URL(country, urlIdentifier)}`;

    // Create AbortController for timeout (Edge Runtime compatible)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Edge case: Handle error responses (404, 400, 500) gracefully
    if (!response.ok) {
      if (response.status === 404 || response.status === 400 || response.status === 500) {
        return null;
      }
      return null;
    }

    const data = await response.json();

    // Edge case: Validate productId exists and is valid
    if (data && data?.productId) {
      return data;
    }

    return null;
  } catch (error) {
    // Handle timeout or network errors gracefully
    if (error.name === "AbortError") {
      console.log("Timeout fetching product by URL");
    } else {
      // Edge case: Catch any unexpected errors (network errors, timeouts, etc.)
      console.log("Error in getProductByUrlAPI:", error);
    }
    return null;
  }
};
/**
 * Fetches breadcrumb details from the server.
 *
 * @param {Object} params - The parameters to include in the request URL.
 * @returns {Promise<Object>} A promise that resolves to the breadcrumb details.
 */
export const getBreadCrumbs = async (params) => {
  const res = await getProductByUrlAPI(params.catalogId, params.urlIdentifierFromParams);
  return res?.breadCrumb;
};
/**
 * Fetches Pay Later configuration.
 *
 * @returns {Promise<Object>} Pay Later config response
 */
export async function getPayLaterConfig() {
  try {
    const res = await InterceptorClient.getGatewayInstance().get(`${PAY_LATER_CONFIG()}`, false);
    return res;
  } catch (ex) {
    throw new Error({ ex, isSuccess: true });
  }
}

/**
 * API for fetching SWIPE info.
 *
 * @returns {Promise<Object>} - A promise that resolves to the JSON response containing SWIPE info data.
 */
export const checkIsSwipeEnable = async () => {
  try {
    return await InterceptorClient.getFNTSInstance().get(IS_SWIPE_ENABLE(), false);
  } catch (error) {
    console.log(error);
  }
};
